<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from "vue";

const props = defineProps<{
  open: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const viewportRef = ref<HTMLElement | null>(null);
const trackRef = ref<HTMLElement | null>(null);
const sectionRef = ref<HTMLElement | null>(null);

/** 首尾相接需要覆盖「屏宽 + 一个画卷周期」，多数屏幕 2 份足够，超宽屏补到 3 份 */
const copyCount = ref(3);
/** 展开 / 收起动画状态 */
const entering = ref(false);
const leaving = ref(false);

const LEAVE_DURATION = 620;
const CLICK_DRAG_THRESHOLD = 6;

let imageWidth = 0;
/** 目标内容横坐标（屏幕左缘对应的内容位置），渲染前才归一化 */
let targetOffset = 0;
/** 平滑跟随的当前渲染坐标 */
let renderedOffset = 0;
let rafId = 0;
let leaveTimer: number | undefined;

let dragging = false;
let dragPointerId = -1;
let dragStartX = 0;
let dragStartTarget = 0;
let dragDistance = 0;
let suppressClick = false;

function normalize(value: number): number {
  if (imageWidth <= 0) return 0;
  return ((value % imageWidth) + imageWidth) % imageWidth;
}

function renderFrame(): void {
  if (!trackRef.value) return;
  // 整像素渲染，避免亚像素重采样让文字出现虚影
  trackRef.value.style.transform = `translateX(${-Math.round(normalize(renderedOffset))}px)`;
}

/** rAF 平滑：渲染坐标以阻尼跟随目标坐标，停止时把两者一起归一化（视觉不变） */
function ensureRaf(): void {
  if (rafId) return;
  const step = () => {
    const diff = targetOffset - renderedOffset;
    if (Math.abs(diff) < 0.5) {
      renderedOffset = targetOffset = normalize(targetOffset);
      renderFrame();
      rafId = 0;
      return;
    }
    renderedOffset += diff * 0.26;
    renderFrame();
    rafId = window.requestAnimationFrame(step);
  };
  rafId = window.requestAnimationFrame(step);
}

function setTarget(value: number, immediate = false): void {
  targetOffset = value;
  if (immediate) {
    renderedOffset = normalize(targetOffset);
    targetOffset = renderedOffset;
    renderFrame();
    return;
  }
  ensureRaf();
}

function firstImage(): HTMLImageElement | null {
  return trackRef.value?.querySelector("img") ?? null;
}

function measureImageWidth(): number {
  const img = firstImage();
  if (!img) return 0;
  const rect = img.getBoundingClientRect();
  return rect.width > 0 ? rect.width : 0;
}

/** 初始位置与开屏一致：画面右缘对齐长卷右端 */
function resetToRightAligned(): void {
  const viewportWidth = viewportRef.value?.clientWidth ?? window.innerWidth;
  imageWidth = measureImageWidth();
  if (imageWidth <= 0) return;
  copyCount.value = imageWidth > viewportWidth * 1.05 ? 2 : 3;
  setTarget(-viewportWidth, true);
}

function handleResize(): void {
  const previousWidth = imageWidth;
  const nextWidth = measureImageWidth();
  if (nextWidth <= 0) return;
  const ratio = previousWidth > 0 ? normalize(targetOffset) / previousWidth : 0;
  imageWidth = nextWidth;
  setTarget(ratio * nextWidth, true);
}

function handlePointerDown(event: PointerEvent): void {
  if (dragging || imageWidth <= 0) return;
  // 点在关闭按钮等交互元素上时不进入拖拽
  if ((event.target as HTMLElement).closest("button")) return;
  dragging = true;
  dragPointerId = event.pointerId;
  dragStartX = event.clientX;
  dragStartTarget = targetOffset;
  dragDistance = 0;
  sectionRef.value?.setPointerCapture(event.pointerId);
}

function handlePointerMove(event: PointerEvent): void {
  if (!dragging || event.pointerId !== dragPointerId) return;
  const dx = event.clientX - dragStartX;
  dragDistance = Math.max(dragDistance, Math.abs(dx));
  setTarget(dragStartTarget - dx);
}

function handlePointerEnd(event: PointerEvent): void {
  if (event.pointerId !== dragPointerId) return;
  dragging = false;
  dragPointerId = -1;
  suppressClick = dragDistance > CLICK_DRAG_THRESHOLD;
}

function handleSectionClick(event: MouseEvent): void {
  if (event.target !== sectionRef.value) return;
  if (suppressClick) {
    suppressClick = false;
    return;
  }
  requestClose();
}

function handleWheel(event: WheelEvent): void {
  if (imageWidth <= 0) return;
  event.preventDefault();
  const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
  setTarget(targetOffset + delta);
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape") requestClose();
}

/** 收起：先播放与开屏同款的卷起动画，结束后再通知父级卸载 */
function requestClose(): void {
  if (leaving.value) return;
  leaving.value = true;
  if (leaveTimer !== undefined) window.clearTimeout(leaveTimer);
  leaveTimer = window.setTimeout(() => {
    leaveTimer = undefined;
    emit("close");
  }, LEAVE_DURATION);
}

function setupViewer(): void {
  const img = firstImage();
  const start = () => resetToRightAligned();
  if (img && img.complete && img.naturalWidth > 0) {
    start();
  } else if (img) {
    img.addEventListener("load", start, { once: true });
  }
  // 布局尚未完成时补测一次
  window.requestAnimationFrame(() => {
    if (imageWidth <= 0) resetToRightAligned();
    entering.value = true;
  });
  window.addEventListener("keydown", handleKeydown);
  window.addEventListener("resize", handleResize);
  sectionRef.value?.addEventListener("wheel", handleWheel, { passive: false });
}

function teardownViewer(): void {
  window.removeEventListener("keydown", handleKeydown);
  window.removeEventListener("resize", handleResize);
  sectionRef.value?.removeEventListener("wheel", handleWheel);
  if (rafId) {
    window.cancelAnimationFrame(rafId);
    rafId = 0;
  }
  if (leaveTimer !== undefined) {
    window.clearTimeout(leaveTimer);
    leaveTimer = undefined;
  }
  dragging = false;
  dragPointerId = -1;
  dragDistance = 0;
  suppressClick = false;
  entering.value = false;
  leaving.value = false;
}

watch(
  () => props.open,
  async (value) => {
    if (value) {
      await nextTick();
      setupViewer();
    } else {
      teardownViewer();
    }
  }
);

onBeforeUnmount(teardownViewer);
</script>

<template>
  <section
    v-if="open"
    ref="sectionRef"
    class="history-scroll-viewer"
    :class="{ 'is-open': entering, 'is-closing': leaving }"
    role="dialog"
    aria-modal="true"
    aria-label="江城记忆长卷"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @pointerup="handlePointerEnd"
    @pointercancel="handlePointerEnd"
    @click="handleSectionClick"
    @dragstart.prevent
    @selectstart.prevent
  >
    <div ref="viewportRef" class="history-scroll-viewer__viewport">
      <div ref="trackRef" class="history-scroll-viewer__track">
        <img
          v-for="copy in copyCount"
          :key="copy"
          class="history-scroll-viewer__image"
          src="/initial-history-scroll.webp"
          alt=""
          draggable="false"
          :aria-hidden="copy > 1 ? 'true' : undefined"
        />
      </div>
    </div>

    <p class="history-scroll-viewer__hint">左右拖动，阅览江城记忆长卷</p>

    <button
      class="history-scroll-viewer__close"
      type="button"
      aria-label="收起长卷"
      title="收起长卷"
      @click.stop="requestClose"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>
  </section>
</template>

<style scoped>
.history-scroll-viewer {
  position: fixed;
  z-index: 5500;
  inset: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: rgba(10, 32, 29, 0);
  user-select: none;
  touch-action: pan-y;
  transition: background 560ms ease;
}

.history-scroll-viewer.is-open {
  background: #0a201d;
}

.history-scroll-viewer.is-closing {
  background: rgba(10, 32, 29, 0);
  pointer-events: none;
}

.history-scroll-viewer::before,
.history-scroll-viewer::after {
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  width: clamp(2.5rem, 8vw, 9rem);
  content: "";
  pointer-events: none;
  opacity: 0;
  transition: opacity 480ms ease;
}

.history-scroll-viewer.is-open::before,
.history-scroll-viewer.is-open::after {
  opacity: 1;
}

.history-scroll-viewer::before {
  left: 0;
  background: linear-gradient(90deg, #0a201d 0%, rgba(10, 32, 29, 0) 100%);
}

.history-scroll-viewer::after {
  right: 0;
  background: linear-gradient(270deg, #0a201d 0%, rgba(10, 32, 29, 0) 100%);
}

/* 展开：从中央细轴向两侧铺开（与开屏收起互为镜像）；收起：向中央卷起 */
.history-scroll-viewer__viewport {
  width: 100%;
  overflow: hidden;
  cursor: grab;
  clip-path: inset(0 50% 0 50%);
  opacity: 0;
  transition:
    clip-path 720ms cubic-bezier(0.22, 0.61, 0.21, 1),
    opacity 200ms ease;
}

.history-scroll-viewer.is-open .history-scroll-viewer__viewport {
  clip-path: inset(0 0 0 0);
  opacity: 1;
}

.history-scroll-viewer.is-closing .history-scroll-viewer__viewport {
  clip-path: inset(0 50% 0 50%);
  opacity: 0;
  transition:
    clip-path 560ms cubic-bezier(0.6, 0.02, 0.35, 1),
    opacity 160ms ease 440ms;
}

.history-scroll-viewer__viewport:active {
  cursor: grabbing;
}

.history-scroll-viewer__track {
  display: flex;
  width: max-content;
  height: clamp(15rem, 58vh, 42rem);
  will-change: transform;
}

.history-scroll-viewer__image {
  display: block;
  width: auto;
  height: 100%;
  max-width: none;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}

.history-scroll-viewer__hint {
  position: absolute;
  z-index: 2;
  bottom: clamp(1.5rem, 6vh, 3.5rem);
  left: 50%;
  margin: 0;
  padding: 0.5rem 1.1rem;
  border: 1px solid rgba(226, 199, 123, 0.28);
  border-radius: 999px;
  color: rgba(245, 239, 217, 0.78);
  background: rgba(10, 32, 29, 0.62);
  backdrop-filter: blur(10px);
  font-size: 0.8rem;
  letter-spacing: 0.12em;
  transform: translateX(-50%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 420ms ease 320ms;
}

.history-scroll-viewer.is-open .history-scroll-viewer__hint {
  opacity: 1;
}

.history-scroll-viewer__close {
  position: absolute;
  z-index: 2;
  top: clamp(0.9rem, 3vh, 1.6rem);
  right: clamp(0.9rem, 3vw, 1.6rem);
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid rgba(245, 239, 217, 0.2);
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: rgba(245, 239, 217, 0.82);
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
  backdrop-filter: blur(10px);
  opacity: 0;
  transition:
    opacity 420ms ease 320ms,
    border-color 200ms ease,
    background 200ms ease,
    color 200ms ease;
}

.history-scroll-viewer.is-open .history-scroll-viewer__close {
  opacity: 1;
}

.history-scroll-viewer__close:hover,
.history-scroll-viewer__close:focus-visible {
  border-color: rgba(226, 199, 123, 0.56);
  color: #102c25;
  background: rgba(226, 199, 123, 0.78);
}

.history-scroll-viewer__close svg {
  width: 18px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-width: 1.8;
}

@media (max-width: 720px) {
  .history-scroll-viewer__track {
    height: clamp(13rem, 46vh, 22rem);
  }

  .history-scroll-viewer__hint {
    font-size: 0.72rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .history-scroll-viewer__viewport,
  .history-scroll-viewer.is-closing .history-scroll-viewer__viewport {
    clip-path: inset(0 0 0 0);
    transition: opacity 220ms ease;
  }
}
</style>
